import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Link } from "react-router-dom";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import Header from "../Header/header";
import Fotter from "../Fotter/fotter";
import API_URL from "../../config";

import {
  Search,
  Filter,
  BookOpen,
  Clock,
  TrendingUp,
  ArrowRight,
  Sparkles,
  Zap,
  Loader2,
  Calendar,
  ChevronRight
} from "lucide-react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

const ViewBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/blogs`);
        const result = await response.json();
        if (response.ok) {
          setBlogs(result.blogs);
          setFilteredBlogs(result.blogs);
          const uniqueCategories = ["All", ...new Set(result.blogs.map((blog) => blog.category))];
          setCategories(uniqueCategories);
        }
      } catch (err) {
        console.error("Failed to fetch blogs", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  useEffect(() => {
    const filtered = blogs.filter(blog => {
      const matchesCategory = selectedCategory === "All" || blog.category === selectedCategory;
      const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.content.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
    setFilteredBlogs(filtered);
  }, [selectedCategory, searchTerm, blogs]);

  const slides = [
    {
      title: "Island Whispers",
      desc: "Uncovering the secret rhythms of the Pearl of the Indian Ocean.",
      img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=2000&auto=format&fit=crop",
      label: "Elite Travel"
    },
    {
      title: "The Spice Route",
      desc: "A sensory expedition through the legendary culinary heart of Ceylon.",
      img: "https://images.unsplash.com/photo-1596797038530-2c39ef821d51?q=80&w=2000&auto=format&fit=crop",
      label: "Gastronomy"
    },
    {
      title: "Ancient Echoes",
      desc: "Walking among kings in the timeless ruins of the Cultural Triangle.",
      img: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?q=80&w=2000&auto=format&fit=crop",
      label: "Heritage"
    }
  ];

  return (
    <div className="bg-white text-sky-950 font-sans selection:bg-sky-100 selection:text-sky-900">
      <Header />

      {/* Premium Hero Slider */}
      <section className="relative h-[85vh] w-full overflow-hidden">
        <Swiper
          modules={[Autoplay, Pagination, Navigation]}
          autoplay={{ delay: 6000, disableOnInteraction: false }}
          pagination={{ clickable: true, el: '.custom-pagination' }}
          navigation={{ nextEl: '.swiper-button-next-custom', prevEl: '.swiper-button-prev-custom' }}
          loop={true}
          className="h-full w-full"
        >
          {slides.map((slide, idx) => (
            <SwiperSlide key={idx}>
              <div className="h-full w-full relative group">
                {/* Background Image */}
                <img
                  src={slide.img}
                  alt={slide.title}
                  className="absolute inset-0 w-full h-full object-cover z-0"
                  onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1200&q=80"; }}
                />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-sky-950/50 via-sky-950/30 to-transparent z-10" />

                {/* Foreground Content */}
                <div className="absolute inset-0 flex items-center justify-center z-20">
                  <div className="text-center text-white px-6 w-full max-w-4xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-[10px] font-black uppercase tracking-[0.2em] mb-8 animate-fade-in shadow-xl">
                      <Sparkles size={12} className="text-sky-300" />
                      {slide.label}
                    </div>
                    <h1 className="text-6xl md:text-8xl font-black mb-6 drop-shadow-2xl animate-slide-up leading-tight tracking-tighter">
                      {slide.title}
                    </h1>
                    <p className="text-xl md:text-2xl font-medium mb-12 max-w-2xl mx-auto drop-shadow-md animate-slide-up-delayed opacity-90">
                      {slide.desc}
                    </p>
                    <button className="px-10 py-5 bg-white text-sky-950 font-black rounded-2xl hover:bg-sky-50 transition-all flex items-center justify-center gap-3 mx-auto shadow-2xl animate-slide-up-more group/btn">
                      READ STORY <ArrowRight size={18} className="group-hover/btn:translate-x-2 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Navigation */}
        <div className="custom-pagination absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-2"></div>
      </section>

      {/* Discovery Hub */}
      <section className="max-w-7xl mx-auto px-6 -mt-20 relative z-20 pb-32">
        <div className="bg-white rounded-[4rem] shadow-2xl border border-gray-50 p-10 md:p-16">

          <div className="flex flex-col lg:flex-row gap-12 items-end justify-between mb-20 border-b border-gray-50 pb-12">
            <div className="max-w-xl">
              <span className="text-sky-500 font-black uppercase text-[10px] tracking-[0.3em] mb-4 inline-block">The Editorial Stream</span>
              <h2 className="text-4xl md:text-5xl font-black text-sky-950 tracking-tight leading-none">Journal of <span className="text-sky-600 italic">Discovery</span></h2>
              <p className="text-sm text-gray-400 font-bold mt-6 leading-relaxed">
                A curated collection of deep-dives, cultural insights, and sensory expeditions across the island's most evocative landscapes.
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-4 w-full lg:w-auto">
              {/* Search */}
              <div className="flex items-center gap-3 bg-gray-50 px-6 py-4 rounded-2xl border border-transparent focus-within:border-sky-200 transition-all min-w-[300px]">
                <Search size={18} className="text-sky-400" />
                <input
                  type="text"
                  placeholder="Find a story..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-transparent outline-none font-bold text-sky-950 placeholder:text-gray-300 w-full"
                />
              </div>
              {/* Category */}
              <div className="flex items-center gap-3 bg-sky-950 px-6 py-4 rounded-2xl border border-transparent shadow-xl shadow-sky-950/20">
                <Filter size={18} className="text-sky-300" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="bg-transparent border-none outline-none font-black text-[10px] uppercase tracking-widest text-white cursor-pointer"
                >
                  {categories.map(c => <option key={c} value={c} className="text-sky-950">{c.toUpperCase()}</option>)}
                </select>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-40 gap-6">
              <Loader2 size={64} className="animate-spin text-sky-200" strokeWidth={1} />
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-300">Synchronizing Master Registry...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
              {filteredBlogs.length > 0 ? (
                filteredBlogs.map((blog, idx) => (
                  <Link
                    key={blog._id}
                    to={`/blog/${blog._id}`}
                    className="group flex flex-col h-full animate-fade-in cursor-pointer"
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    {/* Visual Card */}
                    <div className="relative h-96 rounded-[3rem] overflow-hidden shadow-sm group-hover:shadow-2xl group-hover:-translate-y-2 transition-all duration-700">
                      <img
                        src={blog.coverImageUrl || "https://images.unsplash.com/photo-1544644181-1484b3fdfc62?q=80&w=1000&auto=format&fit=crop"}
                        alt={blog.title}
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                        onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1544644181-1484b3fdfc62?q=80&w=1000&auto=format&fit=crop"; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-sky-950/90 via-sky-950/20 to-transparent"></div>

                      <div className="absolute top-8 left-8">
                        <span className="px-4 py-1.5 bg-white/20 backdrop-blur-md rounded-full text-[9px] font-black text-white uppercase tracking-widest border border-white/20">
                          {blog.category}
                        </span>
                      </div>

                      <div className="absolute bottom-8 left-8 right-8 text-white">
                        <div className="flex items-center gap-2 text-sky-300 mb-3">
                          <TrendingUp size={12} />
                          <span className="text-[8px] font-black uppercase tracking-widest">Featured Story</span>
                        </div>
                        <h3 className="text-2xl font-black tracking-tight leading-tight group-hover:text-sky-300 transition-colors">{blog.title}</h3>
                        <div className="mt-6 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-xl bg-sky-400 flex items-center justify-center text-sky-950 text-[10px] font-black uppercase">
                              {blog.author.charAt(0)}
                            </div>
                            <span className="text-[10px] font-black uppercase tracking-widest">{blog.author}</span>
                          </div>
                          <ChevronRight size={20} className="text-sky-300" />
                        </div>
                      </div>
                    </div>

                    {/* Content Snippet */}
                    <div className="pt-8 px-4 flex flex-col flex-grow">
                      <p className="text-gray-400 font-serif text-lg leading-relaxed line-clamp-3 mb-8 italic">
                        "{blog.content}"
                      </p>
                      <div className="mt-auto flex items-center gap-6 text-[9px] font-black text-gray-300 uppercase tracking-widest border-t border-gray-50 pt-6 group-hover:text-sky-500 transition-colors">
                        <span className="flex items-center gap-2"><Calendar size={14} /> MARCH 2026</span>
                        <span className="flex items-center gap-2"><Clock size={14} /> 5 MIN READ</span>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="col-span-full py-40 flex flex-col items-center justify-center bg-gray-50/50 rounded-[4rem] border-2 border-dashed border-gray-100 gap-6">
                  <div className="w-24 h-24 bg-white rounded-full shadow-inner flex items-center justify-center text-gray-200">
                    <BookOpen size={48} />
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-black text-sky-950 italic">The Page is Blank</h3>
                    <p className="text-xs text-gray-400 font-bold mt-2">Our chroniclers haven't documented this specific discovery yet. Adjust your search parameters.</p>
                    <button
                      onClick={() => { setSelectedCategory("All"); setSearchTerm(""); }}
                      className="mt-8 px-8 py-3.5 bg-sky-950 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-sky-950/20"
                    >
                      RESET DISCOVERY
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Narrative Membership */}
      <section className="bg-sky-950 py-32 mb-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-sky-900 rounded-full -mr-64 -mt-64 opacity-20 blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-sky-800 rounded-full -ml-64 -mb-64 opacity-10 blur-3xl" />

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
          <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight mb-8">Join the Chroniclers</h2>
          <p className="text-lg text-sky-200/60 font-medium mb-12 leading-relaxed">
            Receive weekly sensory reports from the island, featuring high-definition narratives and secret destinations delivered directly to your inner circle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <input
              type="email"
              placeholder="Enter your email for the inner circle..."
              className="flex-grow bg-white/5 border border-white/10 rounded-2xl px-6 py-5 text-white outline-none focus:ring-4 focus:ring-sky-500/20 transition-all font-bold placeholder:text-sky-200/20"
            />
            <button className="bg-sky-500 text-white px-10 py-5 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-sky-400 transition-all shadow-2xl shadow-sky-500/20 active:scale-95 flex items-center justify-center gap-3">
              SUBSCRIBE <Zap size={14} className="fill-current" />
            </button>
          </div>
        </div>
      </section>

      <Fotter />

      {/* Global Animations */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { 
          from { opacity: 0; transform: translateY(40px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        .animate-fade-in { animation: fadeIn 0.8s ease-out forwards; }
        .animate-slide-up { animation: slideUp 1s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-slide-up-delayed { animation: slideUp 1s cubic-bezier(0.16, 1, 0.3, 1) forwards 0.2s; opacity: 0; }
        .animate-slide-up-more { animation: slideUp 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards 0.4s; opacity: 0; }
        
        .swiper-pagination-bullet { background: white !important; opacity: 0.3 !important; }
        .swiper-pagination-bullet-active { opacity: 1 !important; width: 24px !important; border-radius: 4px !important; }
      `}</style>
    </div >
  );
};

export default ViewBlogs;
